import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  FaChartLine, FaNewspaper, FaUniversity, FaWallet,
  FaLock, FaShoppingCart, FaCog, FaSignOutAlt, FaShieldAlt,
  FaCalculator, FaChevronLeft, FaChevronRight, FaBolt
} from 'react-icons/fa';
import AIChatbox from '../components/AIChatbox';
import MonthlyGovernance from '../components/MonthlyGovernance';
import { useAuth } from '../hooks/useAuth';

const sidebarItems = [
  { icon: <FaChartLine />, label: 'Dashboard', path: '/', group: 'main' },
  { icon: <FaBolt />, label: 'Trading', path: '/trading', group: 'main' },
  { icon: <FaUniversity />, label: 'Bank Intelligence', path: '/bank', group: 'finance' },
  { icon: <FaWallet />, label: 'Wealth Engine', path: '/wealth', group: 'finance' },
  { icon: <FaNewspaper />, label: 'Intelligence Feed', path: '/intelligence', group: 'finance' },
  { icon: <FaShoppingCart />, label: 'Order Tracker', path: '/orders', group: 'tools' },
  { icon: <FaLock />, label: 'Password Vault', path: '/vault', group: 'tools' },
  { icon: <FaCalculator />, label: 'Impact Calc', path: '/calculator', group: 'tools' },
  { icon: <FaCog />, label: 'Settings', path: '/settings', group: 'tools' },
];

const groups = [
  { key: 'main', label: null },
  { key: 'finance', label: 'Finance' },
  { key: 'tools', label: 'Tools' },
];

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    try { await logout(); navigate('/login'); }
    catch (err) { console.error('Logout failed:', err); }
  };

  return (
    <div className="min-h-screen bg-dark-700 font-inter text-frost-900 flex overflow-hidden">
      <MonthlyGovernance />

      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-[72px]' : 'w-[240px]'} bg-dark-600 flex flex-col h-screen sticky top-0 transition-all duration-400 ease-out border-r border-white/[0.04]`}>
        {/* Logo */}
        <div className={`p-4 flex items-center ${collapsed ? 'justify-center' : 'gap-3'} h-16`}>
          <div className="w-9 h-9 bg-gradient-to-br from-accent-400 to-accent-600 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-glow">
            <FaShieldAlt className="text-sm" />
          </div>
          {!collapsed && (
            <div className="animate-fade-in overflow-hidden">
              <span className="text-sm font-bold text-frost-900 tracking-tight block leading-none">Strategic</span>
              <span className="text-[9px] text-accent-400 font-semibold uppercase tracking-[0.12em] leading-none mt-0.5 block">Finance OS</span>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 overflow-y-auto no-scrollbar pb-2">
          {groups.map(group => {
            const items = sidebarItems.filter(i => i.group === group.key);
            if (items.length === 0) return null;
            return (
              <div key={group.key} className={group.label ? 'mt-5' : ''}>
                {group.label && !collapsed && (
                  <p className="px-3 mb-1.5 text-[9px] font-semibold text-frost-400 uppercase tracking-[0.15em]">{group.label}</p>
                )}
                {group.label && collapsed && <div className="mx-3 mb-2 mt-1 h-px bg-white/[0.04]" />}
                {items.map((item, index) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={index}
                      onClick={() => navigate(item.path)}
                      title={collapsed ? item.label : undefined}
                      className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-2.5 px-3 py-2 rounded-lg transition-all duration-200 group mb-0.5 relative ${isActive
                        ? 'bg-accent-500/10 text-accent-300'
                        : 'text-frost-500 hover:bg-white/[0.03] hover:text-frost-800'
                      }`}
                    >
                      {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-4 bg-accent-400 rounded-r-full" />}
                      <div className={`text-[15px] flex-shrink-0 transition-colors ${isActive ? 'text-accent-400' : 'text-frost-400 group-hover:text-frost-600'}`}>
                        {item.icon}
                      </div>
                      {!collapsed && (
                        <span className={`text-[13px] tracking-tight whitespace-nowrap ${isActive ? 'font-semibold' : 'font-medium'}`}>
                          {item.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-2 border-t border-white/[0.04]">
          {user && !collapsed && (
            <div className="px-3 py-1.5 mb-1">
              <p className="text-[10px] text-frost-400 font-medium truncate">{user.email}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-frost-400 hover:text-accent-300 hover:bg-accent-500/5 transition-all text-sm"
          >
            <FaSignOutAlt className="flex-shrink-0 text-[13px]" />
            {!collapsed && <span className="font-medium text-[13px]">Sign Out</span>}
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center py-2 rounded-lg text-frost-400 hover:text-frost-700 hover:bg-white/[0.03] transition-all mt-1"
          >
            {collapsed ? <FaChevronRight className="text-[10px]" /> : <FaChevronLeft className="text-[10px]" />}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <header className="h-14 glass border-b border-white/[0.04] flex items-center justify-between px-6 shrink-0 z-10">
          <div className="flex items-center gap-2.5">
            <div className="w-1.5 h-1.5 rounded-full bg-mint-500 animate-pulse-soft shadow-glow-mint"></div>
            <span className="text-[11px] font-medium text-frost-500">System Online</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[11px] font-semibold text-frost-800">Commander</p>
              <p className="text-[10px] text-frost-400">Founder</p>
            </div>
            <div className="w-8 h-8 bg-gradient-to-br from-accent-400 to-accent-600 rounded-lg flex items-center justify-center font-bold text-white text-[11px] shadow-glow">
              KJ
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6 bg-dark-700">
          <div className="max-w-[1400px] mx-auto animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
      <AIChatbox />
    </div>
  );
};

export default MainLayout;
